import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Check, Users, ClipboardList, FileText } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: ClipboardList, title: "Comprehensive assessment", body: "We begin with a relationship survey for both of you — separately. It surfaces strengths, blind spots, and where to spend our time." },
  { icon: FileText, title: "Weekly take-home work", body: "Each session ends with scripture, questions, and practical assignments to take back into your home — because the real work happens between sessions." },
  { icon: Users, title: "Built for couples in any season", body: "Newly married, weathered, or in crisis — the framework adapts to where you are. We meet you there, not where we wish you were." },
];

const WHAT_WE_COVER = [
  "Identity in Christ — who you are before who you are together",
  "Love & Respect dynamics (Dr. Emerson Eggerichs framework)",
  "Communication that builds rather than wounds",
  "Conflict, repentance, and rebuilding trust",
  "Money, expectations, in-laws, sex, parenting",
  "Spiritual leadership and walking with God as a couple",
];

export default function MarriageMentoring() {
  const [form, setForm] = useState({
    groom_name: "", bride_name: "", email: "", phone: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Please tell us briefly what brings you to counseling.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/inquiries/marriage", {
        service: "marriage-mentoring",
        program: "per-session",
        ...form,
      });
      setSent(true);
      toast.success("Inquiry sent. Cody will be in touch soon.");
      setForm({ groom_name: "", bride_name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again or email thedisciplelifems@gmail.com");
    } finally {
      setBusy(false);
    }
  };

  const input = (key, label, type = "text", required = true) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`mm-${key}-input`}
        className="w-full px-4 py-3 outline-none"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
      />
    </div>
  );

  return (
    <div data-testid="marriage-mentoring-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <Link to="/counseling" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-10" style={{ color: "var(--ink-2)" }}>
          <ArrowLeft size={14} /> All counseling
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Marriage Counseling · Certified Mentor</div>
            <h1 className="font-serif-display tracking-[-0.045em] leading-[0.88]" style={{ color: "var(--ink)" }}>
              <span className="block text-5xl sm:text-6xl lg:text-8xl">MARRIAGE</span>
              <span className="block text-5xl sm:text-6xl lg:text-8xl">
                <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>mentoring.</span>
              </span>
            </h1>
            <p className="mt-8 text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
              Christ-centered marriage mentoring for couples in every season — newly married, weathered,
              or walking through crisis. Cody and Samantha are <strong style={{ color: "var(--ink)" }}>certified mentors</strong> in
              the <em style={{ color: "var(--gold)" }}>Dare to be Different</em> program by Matt &amp; Pam Loehr,
              partnered with Dr. Emerson Eggerichs's <em>Love &amp; Respect</em> ministry.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
              This is not therapy in the clinical sense. It's biblical mentoring — built on scripture, sharpened by
              decades of research, and walked out alongside another couple who has stayed in the trenches with you.
            </p>
          </div>

          {/* Pricing card */}
          <div className="lg:col-span-5">
            <div className="card-surface p-10 sticky top-8" style={{ borderColor: "var(--ember)" }}>
              <div className="overline mb-3">Investment</div>
              <div className="flex items-baseline gap-2">
                <span className="font-serif-display text-6xl" style={{ color: "var(--ink)" }}>$100</span>
                <span className="text-sm" style={{ color: "var(--muted)" }}>· per 90-min session</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>
                Programs typically run <strong style={{ color: "var(--ink)" }}>12 to 20 weeks</strong> depending on what
                your marriage needs. Includes the initial relationship survey and weekly take-home material.
              </p>
              <div className="editorial-rule my-6" />
              <ul className="space-y-2 text-sm" style={{ color: "var(--ink-2)" }}>
                <li className="flex items-start gap-2"><Check size={16} style={{ color: "var(--olive)", marginTop: 2, flexShrink: 0 }} /> Couple's relationship survey included</li>
                <li className="flex items-start gap-2"><Check size={16} style={{ color: "var(--olive)", marginTop: 2, flexShrink: 0 }} /> Weekly take-home work</li>
                <li className="flex items-start gap-2"><Check size={16} style={{ color: "var(--olive)", marginTop: 2, flexShrink: 0 }} /> In-person or video sessions</li>
                <li className="flex items-start gap-2"><Check size={16} style={{ color: "var(--olive)", marginTop: 2, flexShrink: 0 }} /> No long-term contract</li>
              </ul>
              <a href="#mm-form" data-testid="mm-cta-start" className="btn-primary mt-8 w-full justify-center">
                Inquire today
              </a>
              <p className="mt-4 text-[0.65rem] uppercase tracking-[0.3em] text-center" style={{ color: "var(--gold)" }}>
                Certified Dare to be Different Mentor
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="overline mb-3">What to expect</div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
            A real plan, <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>not just talk.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {FEATURES.map((f, i) => (
              <div key={i} className="card-surface p-8">
                <div className="overline mb-3" style={{ fontSize: "0.65rem" }}>0{i + 1}</div>
                <f.icon size={28} strokeWidth={1.5} style={{ color: "var(--ember)" }} />
                <h3 className="font-serif-display text-2xl mt-4 leading-tight" style={{ color: "var(--ink)" }}>{f.title}</h3>
                <p className="mt-3 leading-relaxed text-sm" style={{ color: "var(--ink-2)" }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE COVER */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24">
        <div className="overline mb-3">What we'll work on together</div>
        <h2 className="font-serif-display text-4xl md:text-6xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
          The full curriculum.
        </h2>
        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
          {WHAT_WE_COVER.map((t, i) => (
            <li key={i} className="flex items-start gap-3 text-base md:text-lg" style={{ color: "var(--ink-2)" }}>
              <span className="font-serif-display text-xl" style={{ color: "var(--ember)" }}>{String(i + 1).padStart(2, "0")}</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* MISSION QUOTE */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center">
        <p className="font-serif-display text-3xl md:text-5xl leading-snug italic" style={{ color: "var(--ink)" }}>
          "To strengthen families by building strong, Christ-centered marriages
          through biblical marriage mentoring."
        </p>
        <div className="overline mt-8">The mission we share with Matt &amp; Pam Loehr</div>
      </section>

      {/* INQUIRY FORM */}
      <section id="mm-form" style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
          <div className="overline mb-3">Inquire</div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
            Start the conversation.
          </h2>
          <p className="mt-4 max-w-xl" style={{ color: "var(--ink-2)" }}>
            Whatever's going on at home — confused, frustrated, hopeful, hurting — we'd love to hear from you.
            Cody will personally respond within a couple of days to set a no-pressure intro call.
          </p>

          {sent ? (
            <div data-testid="mm-form-success" className="card-surface p-10 mt-12 text-center">
              <Check size={40} strokeWidth={1.5} className="mx-auto" style={{ color: "var(--olive)" }} />
              <h3 className="font-serif-display text-3xl mt-4" style={{ color: "var(--ink)" }}>Inquiry sent.</h3>
              <p className="mt-3" style={{ color: "var(--ink-2)" }}>Thank you for the courage to reach out. Cody will be in touch soon.</p>
              <button onClick={() => setSent(false)} className="btn-ghost mt-6">Send another</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="card-surface p-8 lg:p-10 mt-12 space-y-5" data-testid="mm-inquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {input("groom_name", "Husband's name")}
                {input("bride_name", "Wife's name")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {input("email", "Email", "email")}
                {input("phone", "Phone", "tel")}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>What brings you to counseling *</label>
                <textarea
                  rows={5}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="mm-message-input"
                  placeholder="A sentence or two is enough. Whatever you share stays between us."
                  className="w-full px-4 py-3 outline-none"
                  style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
                />
              </div>
              <button type="submit" disabled={busy} data-testid="mm-submit-button" className="btn-primary w-full justify-center disabled:opacity-50">
                {busy ? "Sending…" : "Send inquiry"}
              </button>
              <p className="text-xs" style={{ color: "var(--muted)" }}>
                Confidential. We will reach out by phone or email. Your story is safe with us.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
